<template>
  <div>
    <div class="border-surface rounded-md border-2 p-2">
      <div
        class="flex w-full justify-between"
        :class="{ 'pointer-events-none opacity-50': isBeingDeleted }"
      >
        <div class="flex grow items-center justify-between">
          <div class="text-sm font-medium">Rol: {{ modelValue.verboseName }}</div>
          <div>
            <PButton
              v-if="canDeleteRole"
              @click.stop="deleteRole"
              :disabled="$attrs.disabled as boolean"
              class="hover:text-error mb-1"
              variant="ghost"
              size="sm"
              :icon="['fas', 'trash-can']"
              tabindex="-1"
              >Rol verwijderen</PButton
            >
          </div>
        </div>
      </div>

      <component
        v-if="formComponent"
        :is="formComponent"
        :disabled="$attrs.disabled"
        v-model="modelValue"
        @role-changed="$emit('roleChanged', knownBusinessEntityContact)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Component } from "vue"
import { computed, onBeforeUnmount, ref } from "vue"
import { type Role } from "@/models/role/role.model"
import PButton from "@/components/elements/PButton.vue"
import { useRoleStore } from "@/store/roles.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"
import type { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import { useDialogStore } from "@/store/dialog.store"
import { RoleTypes } from "@/enums/role-types"
import RoleServiceProviderForm from "@/components/role/RoleServiceProviderForm.vue"
import RoleFinancialOfficerForm from "@/components/role/RoleFinancialOfficerForm.vue"

const modelValue = defineModel<Role>({ required: true })

const props = defineProps({
  knownBusinessEntityContact: {
    type: Object as () => KnownBusinessEntityContact,
    required: true
  }
})

const emit = defineEmits(["roleChanged"])

const roleStore = useRoleStore()
const dialogStore = useDialogStore()

const isBeingDeleted = ref(false)

const formComponent = computed(() => {
  const roleComponentMap: Record<RoleTypes, Component | null> = {
    [RoleTypes.PLANNER]: null,
    [RoleTypes.SERVICE_PROVIDER]: RoleServiceProviderForm,
    [RoleTypes.FINANCIAL_OFFICER]: RoleFinancialOfficerForm
  }
  return roleComponentMap[modelValue.value.type]
})

const rolesProhibitedFromDeletion = ref(new Set([RoleTypes.PLANNER]))
const canDeleteRole = computed(() => {
  return !rolesProhibitedFromDeletion.value.has(modelValue.value.type)
})

let isDeleted = false
function deleteRole() {
  dialogStore.currentDialog = {
    title: "Rol verwijderen",
    body: `Weet je zeker dat je de rol ${modelValue.value.verboseName} wilt verwijderen van ${props.knownBusinessEntityContact.getDisplayText()}?`,
    type: DialogTypes.CONFIRM,
    onConfirm: async () => {
      isDeleted = true
      isBeingDeleted.value = true
      await roleStore.deleteRole(modelValue.value)
      isBeingDeleted.value = false
    },
    textConfirm: "Ja, verwijderen",
    classConfirm: "bg-warning!"
  }
}

onBeforeUnmount(() => {
  if (isDeleted) {
    emit("roleChanged", props.knownBusinessEntityContact)
  }
})
</script>

<style scoped></style>
